import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Box, Typography, Button, Tabs, Tab, Paper, Chip, ToggleButton,
  ToggleButtonGroup, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, FormControl, InputLabel, Select,
  MenuItem, IconButton, Card, CardContent, CardActionArea, Collapse,
  TextField, ClickAwayListener, Menu, ListItemIcon,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddIcon from '@mui/icons-material/Add';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonIcon from '@mui/icons-material/Person';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import BugReportIcon from '@mui/icons-material/BugReport';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { taskActions, projectActions, userActions, laborActions, laborService, commentActions } from '../../store';
import TimelineIcon from '@mui/icons-material/Timeline';
import { TaskCard } from '../../components/taskCard';
import { CreateTaskDialog } from '../../components/createTaskDialog';
import { TagComponent } from '../../components/tags';
import { Timeline } from '../../components/timeline';
import {
  getWeekStart, getWeekEnd, formatWeekLabel, formatMonthLabel,
  formatQuarterLabel, getWeeksInMonth, getQuarterMonths, getQuarter,
  getTasksInRange, countTasksInRange, navigateWeek, navigateMonth,
  navigateQuarter, navigateYear, getNextNWeeks,
} from '../../utils/weekUtils';
import { statusColors, laborStatusColors, priorityColors, typeColors, getAvatarColor, surfaces } from '../../theme/tokens';
import { parseTime, formatTime } from '../../utils/timeUtils';

const COLUMNS = [
  { id: 'todo', title: 'To Do' },
  { id: 'in_progress', title: 'In Progress' },
  { id: 'in_review', title: 'In Review' },
  { id: 'done', title: 'Done' },
];

const PRIORITY_CONFIG = {
  low: { bg: 'rgba(45,135,56,0.1)', color: priorityColors.low },
  medium: { bg: 'rgba(233,127,51,0.1)', color: priorityColors.medium },
  high: { bg: 'rgba(229,73,58,0.1)', color: priorityColors.high },
  critical: { bg: 'rgba(206,53,15,0.1)', color: priorityColors.critical },
};

const PRIORITY_ICONS = {
  low: <KeyboardArrowDownIcon sx={{ fontSize: 16, color: priorityColors.low }} />,
  medium: <DragHandleIcon sx={{ fontSize: 16, color: priorityColors.medium }} />,
  high: <KeyboardArrowUpIcon sx={{ fontSize: 16, color: priorityColors.high }} />,
  critical: <KeyboardDoubleArrowUpIcon sx={{ fontSize: 16, color: priorityColors.critical }} />,
};

const TYPE_ICONS = {
  bug: <BugReportIcon sx={{ fontSize: 16, color: typeColors.bug }} />,
  story: <BookmarkIcon sx={{ fontSize: 16, color: typeColors.story }} />,
  task: <CheckBoxIcon sx={{ fontSize: 16, color: typeColors.task }} />,
};

export const Tasks = () => {
  const dispatch = useDispatch();
  const tasks = useSelector((state) => state.tasks.tasks);
  const projects = useSelector((state) => state.projects.projects);
  const allUsers = useSelector((state) => state.users.users);
  const myProfile = useSelector((state) => state.auth.myProfile);

  // Tab state — persisted in URL so reload preserves the active tab
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_NAMES = useMemo(() => ['planner', 'board'], []);
  const activeTab = Math.max(0, TAB_NAMES.indexOf(searchParams.get('tab') || 'planner'));
  const setActiveTab = (idx) => {
    setSearchParams({ tab: TAB_NAMES[idx] || 'planner' }, { replace: true });
  };

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterWorkItemUsers, setFilterWorkItemUsers] = useState([]);

  // Active Work state
  const [viewScale, setViewScale] = useState('week'); // week, month, quarter, year
  const [sprintView, setSprintView] = useState('board'); // board, list
  const [currentDate, setCurrentDate] = useState(getWeekStart(new Date()));

  // Dialogs
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Right-click context menu
  const [contextMenu, setContextMenu] = useState(null); // { mouseX, mouseY, task }
  const handleContextMenu = (e, task) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, task });
  };
  const handleDeleteFromContext = () => {
    if (contextMenu?.task) dispatch(taskActions.deleteTask(contextMenu.task._id));
    setContextMenu(null);
  };

  // Track which task rows are expanded (survives re-renders from state updates)
  const [expandedRows, setExpandedRows] = useState({});
  // Track which planner weeks are collapsed
  const [collapsedWeeks, setCollapsedWeeks] = useState({});

  // Work items map for board cards: { taskId: [workItems] }
  const [workItemsMap, setWorkItemsMap] = useState({});
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Timeline state
  const [timelineMode, setTimelineMode] = useState('tasks'); // 'tasks' or 'people'
  const [timelineWorkItems, setTimelineWorkItems] = useState([]);
  const [timelineRange, setTimelineRange] = useState(() => {
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    start.setDate(1);
    const end = new Date();
    end.setMonth(end.getMonth() + 2);
    end.setDate(0);
    return { start, end };
  });

  const bulkLabor = useSelector((state) => state.labor.bulkLabor);
  const unresolvedCounts = useSelector((state) => state.comments.unresolvedCounts);

  // --- Filtering ---
  const applyFilters = (taskList) => {
    return taskList.filter((t) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchTitle = (t.title || '').toLowerCase().includes(q);
        const matchDesc = (t.description || '').toLowerCase().includes(q);
        const matchKey = (t.taskNumber || '').toLowerCase().includes(q);
        if (!matchTitle && !matchDesc && !matchKey) return false;
      }
      if (selectedProjects.length > 0) {
        const pid = t.project?._id || t.project;
        if (!selectedProjects.includes(pid)) return false;
      }
      if (filterStatus && t.status !== filterStatus) return false;
      if (filterType && t.type !== filterType) return false;
      if (filterPriority && t.priority !== filterPriority) return false;
      if (filterWorkItemUsers.length > 0) {
        const wis = bulkLabor?.[t._id] || [];
        if (!filterWorkItemUsers.some((uid) => wis.some((wi) => (wi.user?._id || wi.user) === uid))) return false;
      }
      return true;
    });
  };

  // Planner: tasks with no startDate
  const plannerTasks = applyFilters((tasks || []).filter((t) => !t.startDate));

  // Active Work: tasks with startDate
  const scheduledTasks = applyFilters((tasks || []).filter((t) => t.startDate));

  // Planner: overdue tasks (scheduled in the past, not done)
  const thisWeekStart = getWeekStart(new Date());
  const overdueTasks = applyFilters((tasks || []).filter((t) =>
    t.startDate && new Date(t.startDate) < thisWeekStart && t.status !== 'done'
  ));
  const unplannedTasks = applyFilters((tasks || []).filter((t) => !t.startDate && t.status !== 'done'));
  const plannerWeeks = useMemo(() => getNextNWeeks(6), []);

  // Current week range
  const weekStart = getWeekStart(currentDate);
  const weekEnd = getWeekEnd(currentDate);
  const weekTasks = getTasksInRange(scheduledTasks, weekStart, weekEnd);

  // Board helpers
  const getColumnTasks = (status) => weekTasks.filter((t) => t.status === status);

  useEffect(() => {
    dispatch(taskActions.getTasks({}));
    dispatch(projectActions.getProjects());
    dispatch(userActions.getUsers());
  }, [dispatch]);

  // Fetch bulk work items for all tasks (used for people icons, filter, and expanded rows)
  useEffect(() => {
    const ids = (tasks || []).map((t) => t._id);
    if (ids.length > 0) {
      dispatch(laborActions.getBulkLabor(ids));
      dispatch(commentActions.getUnresolvedCounts(ids));
    }
  }, [dispatch, tasks]);

  // Fetch work items for all visible week tasks when on board view
  useEffect(() => {
    if (activeTab === 1 && viewScale === 'week' && sprintView === 'board' && weekTasks.length > 0) {
      const fetchAll = async () => {
        const map = {};
        await Promise.all(weekTasks.map(async (t) => {
          try {
            const res = await laborService.getLabor(t._id);
            if (res.data?.length) map[t._id] = res.data;
          } catch {}
        }));
        setWorkItemsMap((prev) => ({ ...prev, ...map }));
      };
      fetchAll();
    }
  }, [activeTab, viewScale, sprintView, weekTasks.length, currentDate, refreshCounter]);

  // Fetch bulk work items for timeline
  useEffect(() => {
    if (activeTab === 2) {
      const scheduledIds = (tasks || []).filter((t) => t.startDate).map((t) => t._id);
      if (scheduledIds.length > 0) {
        laborService.getBulkLabor(scheduledIds).then((res) => {
          setTimelineWorkItems(res.data || []);
        }).catch(() => {});
      }
    }
  }, [activeTab, tasks]);

  const handleDialogClose = () => {
    setOpenCreate(false);
    setSelectedTask(null);
    setRefreshCounter((c) => c + 1);
    dispatch(taskActions.getTasks({}));
    // Re-fetch unresolved comment counts after dialog closes
    const ids = (tasks || []).map((t) => t._id);
    if (ids.length > 0) dispatch(commentActions.getUnresolvedCounts(ids));
  };

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    dispatch(taskActions.updateTaskStatus(draggableId, destination.droppableId));
  };

  // Helper: get unique work item users for a task
  const getWiUsers = (taskId) => {
    const wis = bulkLabor?.[taskId] || [];
    const users = [];
    const seen = new Set();
    wis.forEach((wi) => {
      if (wi.user?._id && !seen.has(wi.user._id)) {
        seen.add(wi.user._id);
        users.push(wi.user);
      }
    });
    return users;
  };

  // Shared planner row content — renders the interactive elements inside a draggable box
  const PlannerRowContent = ({ task }) => {
    const wiUsers = getWiUsers(task._id);
    return (
      <>
        {/* Priority — clickable select */}
        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Select
            value={task.priority || 'medium'}
            size="small"
            variant="standard"
            disableUnderline
            onChange={(e) => { e.stopPropagation(); handleTaskUpdate(task._id, 'priority', e.target.value); }}
            renderValue={(val) => PRIORITY_ICONS[val] || PRIORITY_ICONS.medium}
            sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0, minHeight: 'unset' }, '& .MuiSelect-icon': { display: 'none' } }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' } } }}
          >
            {Object.entries(PRIORITY_ICONS).map(([v, icon]) => (
              <MenuItem key={v} value={v} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{v}</Typography>
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, flexShrink: 0 }}>{task.taskNumber}</Typography>
        {(unresolvedCounts?.[task._id] || 0) > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 11, color: 'error.main' }} />
            <Typography variant="caption" sx={{ color: 'error.main', fontSize: 9, fontWeight: 700 }}>{unresolvedCounts[task._id]}</Typography>
          </Box>
        )}
        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{task.title}</Typography>
        {/* Tag — clickable */}
        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <TagComponent taskId={task._id} taskTags={task.tags || []} projectId={task.project?._id}
            onTagsChange={(ids) => handleTaskUpdate(task._id, 'tags', ids)} />
        </Box>
        {/* Status — clickable select */}
        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Select
            value={task.status || 'todo'}
            size="small"
            variant="standard"
            disableUnderline
            onChange={(e) => { e.stopPropagation(); dispatch(taskActions.updateTaskStatus(task._id, e.target.value)); }}
            renderValue={(val) => {
              const cfg = statusColors[val] || statusColors.todo;
              return (
                <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.25, borderRadius: 1, bgcolor: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 600 }}>
                  {cfg.label}
                </Box>
              );
            }}
            sx={{ '& .MuiSelect-select': { py: 0, minHeight: 'unset' }, '& .MuiSelect-icon': { display: 'none' } }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' } } }}
          >
            {Object.entries(statusColors).map(([v, cfg]) => (
              <MenuItem key={v} value={v} sx={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </Box>
        {/* Worker avatars */}
        {wiUsers.length > 0 && (
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            {wiUsers.slice(0, 3).map((u, i) => (
              <Avatar key={u._id} sx={{ width: 20, height: 20, fontSize: 9, fontWeight: 600, bgcolor: getAvatarColor(u._id), border: '2px solid', borderColor: 'background.paper', ml: i > 0 ? -0.5 : 0 }}>
                {u.name?.[0]?.toUpperCase()}
              </Avatar>
            ))}
            {wiUsers.length > 3 && (
              <Avatar sx={{ width: 20, height: 20, fontSize: 8, bgcolor: 'text.disabled', border: '2px solid', borderColor: 'background.paper', ml: -0.5 }}>+{wiUsers.length - 3}</Avatar>
            )}
          </Box>
        )}
      </>
    );
  };

  const handlePlannerDragEnd = (result) => {
    if (!result.destination) return;
    const { draggableId, destination, source } = result;
    if (destination.droppableId === source.droppableId) return;
    const dest = destination.droppableId;
    if (dest === 'backlog') {
      dispatch(taskActions.editTask(draggableId, { startDate: null, endDate: null }));
    } else if (dest.startsWith('week-')) {
      const ws = new Date(dest.replace('week-', ''));
      const we = getWeekEnd(ws);
      const fmt = (d) => d.toISOString().split('T')[0];
      dispatch(taskActions.editTask(draggableId, { startDate: fmt(ws), endDate: fmt(we) }));
    }
  };

  // Navigation
  const navigate = (direction) => {
    switch (viewScale) {
      case 'week': setCurrentDate(navigateWeek(currentDate, direction)); break;
      case 'month': setCurrentDate(navigateMonth(currentDate, direction)); break;
      case 'quarter': setCurrentDate(navigateQuarter(currentDate, direction)); break;
      case 'year': setCurrentDate(navigateYear(currentDate, direction)); break;
    }
  };

  const goToToday = () => setCurrentDate(getWeekStart(new Date()));

  const getNavigationLabel = () => {
    switch (viewScale) {
      case 'week': return formatWeekLabel(currentDate);
      case 'month': return formatMonthLabel(currentDate.getFullYear(), currentDate.getMonth());
      case 'quarter': return formatQuarterLabel(currentDate.getFullYear(), getQuarter(currentDate));
      case 'year': return `${currentDate.getFullYear()}`;
      default: return '';
    }
  };

  // --- Inline-editable title for table rows ---
  const InlineTitle = ({ value, onChange }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const inputRef = useRef(null);
    useEffect(() => { setDraft(value || ''); }, [value]);
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
    const commit = () => {
      setEditing(false);
      if (draft.trim() && draft !== value) onChange(draft.trim());
      else setDraft(value || '');
    };
    if (!editing) {
      return (
        <Typography variant="body2" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          sx={{ fontWeight: 500, cursor: 'text', px: 0.5, py: 0.25, borderRadius: 0.5, '&:hover': { bgcolor: 'action.hover' } }}>
          {value || 'Untitled'}
        </Typography>
      );
    }
    return (
      <ClickAwayListener onClickAway={commit}>
        <TextField inputRef={inputRef} size="small" variant="standard" value={draft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { commit(); e.preventDefault(); }
            if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          }}
          inputProps={{ style: { fontSize: 13, fontWeight: 500, padding: '2px 4px' } }}
          sx={{ width: '100%' }}
        />
      </ClickAwayListener>
    );
  };

  const menuPaperSx = { borderRadius: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' };

  const handleTaskUpdate = (taskId, field, value) => {
    dispatch(taskActions.editTask(taskId, { [field]: value }));
  };

  // --- Expandable Task Row ---
  const TaskRow = ({ task }) => {
    const expanded = !!expandedRows[task._id];
    const taskWorkItems = bulkLabor?.[task._id] || [];

    const toggleExpand = (e) => {
      e.stopPropagation();
      setExpandedRows((prev) => ({ ...prev, [task._id]: !prev[task._id] }));
    };

    const handleWiUpdate = (wiId, data) => {
      dispatch(laborActions.updateLabor(wiId, data));
      // Re-fetch after a short delay
      setTimeout(() => {
        const ids = (tasks || []).map((t) => t._id);
        if (ids.length) dispatch(laborActions.getBulkLabor(ids));
      }, 600);
    };

    const handleAddWorkItem = () => {
      dispatch(laborActions.createLabor({ task: task._id, user: myProfile?._id, status: 'pending' }));
      setTimeout(() => {
        const ids = (tasks || []).map((t) => t._id);
        if (ids.length) dispatch(laborActions.getBulkLabor(ids));
      }, 600);
    };

    // Unique work item users for people icons
    const wiUsers = [];
    const seenIds = new Set();
    taskWorkItems.forEach((wi) => {
      if (wi.user?._id && !seenIds.has(wi.user._id)) {
        seenIds.add(wi.user._id);
        wiUsers.push(wi.user);
      }
    });

    return (
      <>
        <TableRow hover onContextMenu={(e) => handleContextMenu(e, task)} sx={{ '& > td': { borderBottom: expanded ? 'none' : undefined } }}>
          <TableCell sx={{ width: 36, px: 0.5 }}>
            <IconButton size="small" onClick={toggleExpand} sx={{ p: 0.25 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </TableCell>
          {/* Key — click opens dialog */}
          <TableCell onClick={() => setSelectedTask(task)} sx={{ cursor: 'pointer' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{task.taskNumber || '--'}</Typography>
              {(unresolvedCounts?.[task._id] || 0) > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 12, color: 'error.main' }} />
                  <Typography variant="caption" sx={{ color: 'error.main', fontSize: 10, fontWeight: 700 }}>
                    {unresolvedCounts[task._id]}
                  </Typography>
                </Box>
              )}
            </Box>
          </TableCell>
          {/* Title — inline editable */}
          <TableCell>
            <InlineTitle value={task.title} onChange={(val) => handleTaskUpdate(task._id, 'title', val)} />
          </TableCell>
          {/* Type — inline select */}
          <TableCell>
            <Select value={task.type || 'task'} size="small" variant="standard" disableUnderline
              onChange={(e) => handleTaskUpdate(task._id, 'type', e.target.value)}
              renderValue={(val) => TYPE_ICONS[val] || TYPE_ICONS.task}
              sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 } }}
              MenuProps={{ PaperProps: { sx: menuPaperSx } }}>
              {Object.entries(TYPE_ICONS).map(([v, icon]) => (
                <MenuItem key={v} value={v} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {icon}<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{v}</Typography>
                </MenuItem>
              ))}
            </Select>
          </TableCell>
          {/* Status — inline select */}
          <TableCell>
            <Select value={task.status || 'todo'} size="small" variant="standard" disableUnderline
              onChange={(e) => dispatch(taskActions.updateTaskStatus(task._id, e.target.value))}
              renderValue={(val) => {
                const cfg = statusColors[val] || statusColors.todo;
                return (
                  <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: 12 }}>
                    {cfg.label}
                  </Box>
                );
              }}
              MenuProps={{ PaperProps: { sx: menuPaperSx } }}>
              {Object.entries(statusColors).map(([v, cfg]) => (
                <MenuItem key={v} value={v} sx={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</MenuItem>
              ))}
            </Select>
          </TableCell>
          {/* Priority — inline select */}
          <TableCell>
            <Select value={task.priority || 'medium'} size="small" variant="standard" disableUnderline
              onChange={(e) => handleTaskUpdate(task._id, 'priority', e.target.value)}
              renderValue={(val) => PRIORITY_ICONS[val] || PRIORITY_ICONS.medium}
              sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 } }}
              MenuProps={{ PaperProps: { sx: menuPaperSx } }}>
              {Object.entries(PRIORITY_ICONS).map(([v, icon]) => (
                <MenuItem key={v} value={v} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {icon}<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{v}</Typography>
                </MenuItem>
              ))}
            </Select>
          </TableCell>
          {/* People (work item users) */}
          <TableCell>
            <Box sx={{ display: 'flex' }}>
              {wiUsers.slice(0, 4).map((u, i) => (
                <Avatar key={u._id} sx={{
                  width: 22, height: 22, fontSize: 9, fontWeight: 600,
                  bgcolor: getAvatarColor(u._id), border: '2px solid', borderColor: 'background.paper',
                  ml: i > 0 ? -0.75 : 0,
                }}>
                  {u.name?.[0]?.toUpperCase()}
                </Avatar>
              ))}
              {wiUsers.length > 4 && (
                <Avatar sx={{ width: 22, height: 22, fontSize: 9, bgcolor: 'text.disabled', border: '2px solid', borderColor: 'background.paper', ml: -0.75 }}>
                  +{wiUsers.length - 4}
                </Avatar>
              )}
            </Box>
          </TableCell>
          {/* Tags */}
          <TableCell>
            <TagComponent taskId={task._id} taskTags={task.tags || []} projectId={task.project?._id}
              onTagsChange={(ids) => handleTaskUpdate(task._id, 'tags', ids)} />
          </TableCell>
          {/* Project */}
          <TableCell onClick={() => setSelectedTask(task)} sx={{ cursor: 'pointer' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{task.project?.key || '—'}</Typography>
          </TableCell>
        </TableRow>
        {expanded && (
          <TableRow>
            <TableCell colSpan={11} sx={{ py: 0, px: 0, bgcolor: 'action.hover' }}>
              <Collapse in={expanded}>
                <Box sx={{ px: 4, py: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, py: 0.5 }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, py: 0.5 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, py: 0.5 }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, py: 0.5 }}>Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {taskWorkItems.map((wi) => {
                        const st = laborStatusColors[wi.status] || laborStatusColors.pending;
                        return (
                          <TableRow key={wi._id} sx={{ '& td': { py: 0.5 } }}>
                            {/* User — editable select */}
                            <TableCell>
                              <Select value={wi.user?._id || ''} size="small" variant="standard" disableUnderline
                                onChange={(e) => handleWiUpdate(wi._id, { user: e.target.value })}
                                sx={{ fontSize: 12 }}
                                renderValue={(val) => {
                                  const u = (allUsers || []).find((m) => m._id === val) || wi.user;
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <Avatar sx={{ width: 20, height: 20, fontSize: 9, bgcolor: getAvatarColor(u?._id) }}>{u?.name?.[0]?.toUpperCase() || '?'}</Avatar>
                                      <Typography variant="caption">{u?.name || 'Unknown'}</Typography>
                                    </Box>
                                  );
                                }}
                                MenuProps={{ PaperProps: { sx: menuPaperSx } }}>
                                {(allUsers || []).map((m) => (
                                  <MenuItem key={m._id} value={m._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12 }}>
                                    <Avatar sx={{ width: 20, height: 20, fontSize: 9, bgcolor: getAvatarColor(m._id) }}>{m.name?.[0]?.toUpperCase()}</Avatar>
                                    {m.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            {/* Status — editable select */}
                            <TableCell>
                              <Select value={wi.status || 'pending'} size="small" variant="standard" disableUnderline
                                onChange={(e) => handleWiUpdate(wi._id, { status: e.target.value })}
                                renderValue={(val) => {
                                  const c = laborStatusColors[val] || laborStatusColors.pending;
                                  return (
                                    <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.25, borderRadius: 1, bgcolor: c.bg, color: c.color, fontWeight: 600, fontSize: 11 }}>
                                      {c.label}
                                    </Box>
                                  );
                                }}
                                MenuProps={{ PaperProps: { sx: menuPaperSx } }}>
                                {Object.entries(laborStatusColors).map(([v, c]) => (
                                  <MenuItem key={v} value={v} sx={{ color: c.color, fontWeight: 600, fontSize: 12 }}>{c.label}</MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            {/* Time — editable */}
                            <TableCell>
                              <InlineTitle
                                value={wi.time ? formatTime(wi.time) : ''}
                                onChange={(val) => {
                                  const minutes = parseTime(val);
                                  if (minutes !== null) handleWiUpdate(wi._id, { time: minutes });
                                }}
                              />
                            </TableCell>
                            {/* Note — editable */}
                            <TableCell>
                              <InlineTitle
                                value={wi.note || ''}
                                onChange={(val) => handleWiUpdate(wi._id, { note: val })}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {/* Add work item button */}
                  <Button size="small" onClick={handleAddWorkItem}
                    sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
                    + Add work item
                  </Button>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  // --- Task Table (reused in Planner and Active Work list) ---
  const TaskTable = ({ data }) => (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 36, px: 0.5 }} />
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 90 }}>Key</TableCell>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Title</TableCell>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 50 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 120 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 50 }}>Priority</TableCell>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 100 }}>People</TableCell>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Tags</TableCell>
            <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 70 }}>Project</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((task) => <TaskRow key={task._id} task={task} />)}
          {data.length === 0 && (
            <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>No tasks found</Typography>
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // --- Board View ---
  const BoardView = ({ boardTasks }) => (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 2, pb: 2 }}>
        {COLUMNS.map((column) => {
          const colTasks = boardTasks.filter((t) => t.status === column.id);
          return (
            <Paper key={column.id} elevation={0}
              sx={{ flex: 1, minWidth: 0, backgroundColor: surfaces.elevated, borderRadius: 2, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 280px)' }}>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColors[column.id]?.color }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>{column.title}</Typography>
                </Box>
                <Chip label={colTasks.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, backgroundColor: 'divider', color: 'text.secondary' }} />
              </Box>
              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}
                    sx={{ p: 1, flexGrow: 1, overflowY: 'auto', minHeight: 80, backgroundColor: snapshot.isDraggingOver ? 'action.selected' : 'transparent', transition: 'background-color 0.2s' }}>
                    {colTasks.map((task, index) => (
                      <Draggable key={task._id} draggableId={task._id} index={index}>
                        {(provided, snapshot) => (
                          <Box ref={provided.innerRef} {...provided.draggableProps} sx={{ mb: 1 }}>
                            <TaskCard
                              task={task}
                              isDragging={snapshot.isDragging}
                              dragHandleProps={provided.dragHandleProps}
                              unresolvedComments={unresolvedCounts?.[task._id] || 0}
                              workItems={workItemsMap[task._id]}
                              members={allUsers || []}
                              onClick={() => setSelectedTask(task)}
                              onTitleChange={(newTitle) => dispatch(taskActions.editTask(task._id, { title: newTitle }))}
                              onPriorityChange={(newPriority) => dispatch(taskActions.editTask(task._id, { priority: newPriority }))}
                              onStatusChange={(newStatus) => dispatch(taskActions.updateTaskStatus(task._id, newStatus))}
                              onTagsChange={(tagIds) => dispatch(taskActions.editTask(task._id, { tags: tagIds }))}
                              onWorkItemFieldChange={(laborId, field, value) => dispatch(laborActions.updateLabor(laborId, { [field]: value }))}
                              onWorkItemUserChange={(laborId, userId) => {
                                dispatch(laborActions.updateLabor(laborId, { user: userId }));
                              }}
                              onWorkItemStatusChange={(laborId, newStatus) => {
                                dispatch(laborActions.updateLabor(laborId, { status: newStatus }));
                              }}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          );
        })}
      </Box>
    </DragDropContext>
  );

  // --- Month View ---
  const MonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const weeks = getWeeksInMonth(year, month);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {weeks.map((w) => {
          const count = countTasksInRange(scheduledTasks, w.start, w.end);
          const wTasks = getTasksInRange(scheduledTasks, w.start, w.end);
          const startLabel = w.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const endLabel = w.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          return (
            <Card key={w.weekNumber} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
              onClick={() => { setCurrentDate(w.start); setViewScale('week'); }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Week {w.weekNumber}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{startLabel} – {endLabel}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={`${count} tasks`} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, backgroundColor: count > 0 ? 'action.selected' : surfaces.elevated, color: count > 0 ? 'primary.main' : 'text.disabled' }} />
                    {/* Mini status breakdown */}
                    {count > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        {COLUMNS.map((col) => {
                          const n = wTasks.filter((t) => t.status === col.id).length;
                          return n > 0 ? <Box key={col.id} sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusColors[col.id]?.color }} title={`${col.title}: ${n}`} /> : null;
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  // --- Quarter View ---
  const QuarterView = () => {
    const year = currentDate.getFullYear();
    const quarter = getQuarter(currentDate);
    const months = getQuarterMonths(year, quarter);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {months.map((m) => {
          const weeks = getWeeksInMonth(year, m.month);
          const monthStart = new Date(year, m.month, 1);
          const monthEnd = new Date(year, m.month + 1, 0, 23, 59, 59);
          const totalCount = countTasksInRange(scheduledTasks, monthStart, monthEnd);
          return (
            <Card key={m.month} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, cursor: 'pointer' }}
                  onClick={() => { setCurrentDate(new Date(year, m.month, 1)); setViewScale('month'); }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{m.label}</Typography>
                  <Chip label={`${totalCount} tasks`} size="small" sx={{ fontWeight: 600, backgroundColor: totalCount > 0 ? 'action.selected' : surfaces.elevated, color: totalCount > 0 ? 'primary.main' : 'text.disabled' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {weeks.map((w) => {
                    const c = countTasksInRange(scheduledTasks, w.start, w.end);
                    return (
                      <Chip key={w.weekNumber} label={`W${w.weekNumber}: ${c}`} size="small" variant="outlined"
                        onClick={() => { setCurrentDate(w.start); setViewScale('week'); }}
                        sx={{ cursor: 'pointer', fontWeight: 600, borderColor: c > 0 ? 'primary.main' : 'divider', color: c > 0 ? 'primary.main' : 'text.disabled' }} />
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  // --- Year View ---
  const YearView = () => {
    const year = currentDate.getFullYear();
    const quarters = [0, 1, 2, 3];
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {quarters.map((q) => {
          const months = getQuarterMonths(year, q);
          const qStart = new Date(year, q * 3, 1);
          const qEnd = new Date(year, q * 3 + 3, 0, 23, 59, 59);
          const totalCount = countTasksInRange(scheduledTasks, qStart, qEnd);
          return (
            <Card key={q} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, cursor: 'pointer' }}
                  onClick={() => { setCurrentDate(qStart); setViewScale('quarter'); }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Q{q + 1}</Typography>
                  <Chip label={`${totalCount} tasks`} size="small" sx={{ fontWeight: 600, backgroundColor: totalCount > 0 ? 'action.selected' : surfaces.elevated, color: totalCount > 0 ? 'primary.main' : 'text.disabled' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {months.map((m) => {
                    const mStart = new Date(year, m.month, 1);
                    const mEnd = new Date(year, m.month + 1, 0, 23, 59, 59);
                    const c = countTasksInRange(scheduledTasks, mStart, mEnd);
                    return (
                      <Chip key={m.month} label={`${m.label.slice(0, 3)}: ${c}`} size="small" variant="outlined"
                        onClick={() => { setCurrentDate(mStart); setViewScale('month'); }}
                        sx={{ cursor: 'pointer', fontWeight: 600, borderColor: c > 0 ? 'primary.main' : 'divider', color: c > 0 ? 'primary.main' : 'text.disabled' }} />
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Tabs + Create Button */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}>
          <Tab label={`Planner (${overdueTasks.length + unplannedTasks.length})`} sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Board" sx={{ textTransform: 'none', fontWeight: 600 }} />
          {/* <Tab icon={<TimelineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Timeline" sx={{ textTransform: 'none', fontWeight: 600 }} /> */}
        </Tabs>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setOpenCreate(true)}
          sx={{ textTransform: 'none', fontWeight: 600 }}>Create Task</Button>
      </Box>

      {/* Shared filter bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5 }} />,
            sx: { fontSize: 13 },
          }}
          sx={{ minWidth: 180 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Project</InputLabel>
          <Select
            multiple
            value={selectedProjects}
            onChange={(e) => setSelectedProjects(e.target.value)}
            label="Project"
            renderValue={(selected) => selected.length === 0 ? 'All' :
              selected.map((id) => (projects || []).find((p) => p._id === id)?.key || id).join(', ')
            }
          >
            {(projects || []).map((p) => (
              <MenuItem key={p._id} value={p._id}>{p.name} ({p.key})</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="todo">To Do</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="in_review">In Review</MenuItem>
            <MenuItem value="done">Done</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Type">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="bug">Bug</MenuItem>
            <MenuItem value="story">Story</MenuItem>
            <MenuItem value="task">Task</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} label="Priority">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </Select>
        </FormControl>
        {/* Assignee avatar pills */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          {(allUsers || []).map((u) => {
            const isSelected = filterWorkItemUsers.includes(u._id);
            const initials = (u.name || '').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
            return (
              <Avatar
                key={u._id}
                onClick={() => setFilterWorkItemUsers((prev) =>
                  prev.includes(u._id) ? prev.filter((id) => id !== u._id) : [...prev, u._id]
                )}
                sx={{
                  width: 30, height: 30, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  bgcolor: getAvatarColor(u._id),
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'background.paper',
                  opacity: filterWorkItemUsers.length > 0 && !isSelected ? 0.4 : 1,
                  ml: -0.5,
                  transition: 'opacity 0.15s, border-color 0.15s',
                  '&:first-of-type': { ml: 0 },
                  '&:hover': { opacity: 1 },
                }}
                title={u.name}
              >
                {initials}
              </Avatar>
            );
          })}
        </Box>
        {(searchQuery || selectedProjects.length > 0 || filterStatus || filterType || filterPriority || filterWorkItemUsers.length > 0) && (
          <Button size="small" onClick={() => { setSearchQuery(''); setSelectedProjects([]); setFilterStatus(''); setFilterType(''); setFilterPriority(''); setFilterWorkItemUsers([]); }}
            sx={{ textTransform: 'none', color: 'text.secondary', ml: 1 }}>
            Clear
          </Button>
        )}
      </Box>

      {/* ========== PLANNER TAB ========== */}
      {activeTab === 0 && (
        <DragDropContext onDragEnd={handlePlannerDragEnd}>
          <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 220px)' }}>

            {/* --- BACKLOG (left panel) --- */}
            <Paper elevation={0} sx={{ width: '40%', flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Backlog ({overdueTasks.length + unplannedTasks.length})
                </Typography>
              </Box>
              <Droppable droppableId="backlog">
                {(provided, snapshot) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}
                    sx={{ flex: 1, overflowY: 'auto', p: 1, bgcolor: snapshot.isDraggingOver ? 'action.selected' : 'transparent', transition: 'background-color 0.2s' }}>

                    {/* Overdue section */}
                    {overdueTasks.length > 0 && (
                      <>
                        <Typography variant="overline" sx={{ color: 'error.main', px: 0.5, mb: 0.5, display: 'block' }}>
                          Overdue ({overdueTasks.length})
                        </Typography>
                        {overdueTasks.map((task, index) => (
                          <Draggable key={task._id} draggableId={task._id} index={index}>
                            {(provided, snapshot) => (
                              <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                onClick={() => setSelectedTask(task)}
                                onContextMenu={(e) => handleContextMenu(e, task)}
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, mb: 0.5,
                                  borderRadius: 1.5, cursor: 'grab', bgcolor: 'background.paper',
                                  border: '1px solid', borderColor: snapshot.isDragging ? 'primary.main' : 'error.light',
                                  borderLeftWidth: 3, borderLeftColor: 'error.main',
                                  boxShadow: snapshot.isDragging ? 3 : 0,
                                  '&:hover': { borderColor: 'primary.light' },
                                }}>
                                <PlannerRowContent task={task} />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                      </>
                    )}

                    {/* Unplanned section */}
                    <Typography variant="overline" sx={{ color: 'text.secondary', px: 0.5, mb: 0.5, mt: overdueTasks.length > 0 ? 1.5 : 0, display: 'block' }}>
                      Unplanned ({unplannedTasks.length})
                    </Typography>
                    {unplannedTasks.map((task, index) => (
                      <Draggable key={task._id} draggableId={task._id} index={overdueTasks.length + index}>
                        {(provided, snapshot) => (
                          <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                            onClick={() => setSelectedTask(task)}
                            onContextMenu={(e) => handleContextMenu(e, task)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, mb: 0.5,
                              borderRadius: 1.5, cursor: 'grab', bgcolor: 'background.paper',
                              border: '1px solid', borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                              boxShadow: snapshot.isDragging ? 3 : 0,
                              '&:hover': { borderColor: 'primary.light' },
                            }}>
                            <PlannerRowContent task={task} />
                          </Box>
                        )}
                      </Draggable>
                    ))}

                    {overdueTasks.length === 0 && unplannedTasks.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 4 }}>
                        No tasks in backlog
                      </Typography>
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>

            {/* --- WEEKS (right panel) --- */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {plannerWeeks.map((week, weekIdx) => {
                const weekId = `week-${week.start.toISOString()}`;
                const isThisWeek = weekIdx === 0;
                const weekLabel = isThisWeek ? 'This Week' : weekIdx === 1 ? 'Next Week' : '';
                const startLabel = week.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const endLabel = week.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const weekTasksList = getTasksInRange(scheduledTasks, week.start, week.end);

                const isCollapsed = !!collapsedWeeks[weekId];
                const toggleCollapse = () => setCollapsedWeeks((prev) => ({ ...prev, [weekId]: !prev[weekId] }));

                return (
                  <Droppable key={weekId} droppableId={weekId}>
                    {(provided, snapshot) => (
                      <Paper ref={provided.innerRef} {...provided.droppableProps}
                        elevation={0}
                        sx={{
                          mb: 1.5, border: '1px solid', borderRadius: 2,
                          borderColor: snapshot.isDraggingOver ? 'primary.main' : isThisWeek ? 'primary.light' : 'divider',
                          bgcolor: snapshot.isDraggingOver ? 'action.selected' : 'transparent',
                          transition: 'border-color 0.2s, background-color 0.2s',
                        }}>
                        <Box
                          onClick={toggleCollapse}
                          sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', borderBottom: (!isCollapsed && weekTasksList.length > 0) || snapshot.isDraggingOver ? '1px solid' : 'none', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" sx={{ p: 0, width: 20, height: 20 }} onClick={(e) => { e.stopPropagation(); toggleCollapse(); }}>
                              {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ExpandLessIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                            {isThisWeek && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isThisWeek ? 'primary.main' : 'text.primary' }}>
                              {weekLabel ? `${weekLabel} ` : ''}{startLabel} – {endLabel}
                            </Typography>
                          </Box>
                          <Chip label={weekTasksList.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: 'divider', color: 'text.secondary' }} />
                        </Box>
                        <Collapse in={!isCollapsed || snapshot.isDraggingOver}>
                          <Box sx={{ p: weekTasksList.length > 0 || snapshot.isDraggingOver ? 1 : 0, minHeight: snapshot.isDraggingOver ? 48 : 0, transition: 'min-height 0.2s' }}>
                            {weekTasksList.map((task, index) => (
                              <Draggable key={task._id} draggableId={task._id} index={index}>
                                {(provided, snapshot) => (
                                  <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                    onClick={() => setSelectedTask(task)}
                                    sx={{
                                      display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, mb: 0.5,
                                      borderRadius: 1.5, cursor: 'grab', bgcolor: 'background.paper',
                                      border: '1px solid', borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                                      boxShadow: snapshot.isDragging ? 3 : 0,
                                      '&:hover': { borderColor: 'primary.light' },
                                      ...(task.status === 'done' && { opacity: 0.5 }),
                                    }}>
                                    <PlannerRowContent task={task} />
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Box>
                        </Collapse>
                      </Paper>
                    )}
                  </Droppable>
                );
              })}
            </Box>
          </Box>
        </DragDropContext>
      )}

      {/* ========== ACTIVE WORK TAB ========== */}
      {activeTab === 1 && (
        <Box>
          {/* Scale + view toggle + navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <ToggleButtonGroup value={viewScale} exclusive onChange={(e, v) => v && setViewScale(v)} size="small">
              <ToggleButton value="week" sx={{ textTransform: 'none', fontWeight: 600 }}>Week</ToggleButton>
              <ToggleButton value="month" sx={{ textTransform: 'none', fontWeight: 600 }}>Month</ToggleButton>
              <ToggleButton value="quarter" sx={{ textTransform: 'none', fontWeight: 600 }}>Quarter</ToggleButton>
              <ToggleButton value="year" sx={{ textTransform: 'none', fontWeight: 600 }}>Year</ToggleButton>
            </ToggleButtonGroup>

            {/* Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => navigate(-1)}><ChevronLeftIcon /></IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 200, textAlign: 'center', color: 'text.primary' }}>
                {getNavigationLabel()}
              </Typography>
              <IconButton size="small" onClick={() => navigate(1)}><ChevronRightIcon /></IconButton>
              <IconButton size="small" onClick={goToToday} title="Go to today" sx={{ ml: 1 }}><TodayIcon /></IconButton>
            </Box>

            {/* Board/List toggle (only for week view) */}
            {viewScale === 'week' && (
              <ToggleButtonGroup value={sprintView} exclusive onChange={(e, v) => v && setSprintView(v)} size="small">
                <ToggleButton value="board" sx={{ textTransform: 'none' }}><ViewKanbanIcon sx={{ fontSize: 18 }} /></ToggleButton>
                <ToggleButton value="list" sx={{ textTransform: 'none' }}><ListAltIcon sx={{ fontSize: 18 }} /></ToggleButton>
              </ToggleButtonGroup>
            )}
            {viewScale !== 'week' && <Box sx={{ width: 80 }} />}
          </Box>

          {/* Week view */}
          {viewScale === 'week' && (
            sprintView === 'board'
              ? <BoardView boardTasks={weekTasks} />
              : <TaskTable data={weekTasks} />
          )}

          {/* Month view */}
          {viewScale === 'month' && <MonthView />}

          {/* Quarter view */}
          {viewScale === 'quarter' && <QuarterView />}

          {/* Year view */}
          {viewScale === 'year' && <YearView />}
        </Box>
      )}

      {/* ========== TIMELINE TAB ========== */}
      {/* Timeline tab — commented out for now
      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <ToggleButtonGroup value={timelineMode} exclusive onChange={(e, v) => v && setTimelineMode(v)} size="small">
              <ToggleButton value="tasks" sx={{ textTransform: 'none', fontWeight: 600 }}>Tasks</ToggleButton>
              <ToggleButton value="people" sx={{ textTransform: 'none', fontWeight: 600 }}>People</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Timeline
            tasks={applyFilters((tasks || []).filter((t) => t.startDate))}
            workItems={timelineWorkItems}
            users={allUsers}
            viewMode={timelineMode}
            rangeStart={timelineRange.start}
            rangeEnd={timelineRange.end}
            onTaskClick={(task) => setSelectedTask(task)}
          />
        </Box>
      )}
      */}

      {/* Right-click context menu */}
      <Menu
        open={!!contextMenu}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuItem onClick={handleDeleteFromContext} sx={{ color: 'error.main', fontSize: 13 }}>
          <ListItemIcon><DeleteOutlineIcon sx={{ color: 'error.main' }} fontSize="small" /></ListItemIcon>
          Delete task
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <CreateTaskDialog open={openCreate} onClose={handleDialogClose} />
      {selectedTask && (
        <CreateTaskDialog open={Boolean(selectedTask)} onClose={handleDialogClose} editTask={selectedTask} />
      )}
    </Box>
  );
};
